import { createAsyncThunk } from "@reduxjs/toolkit";
import apiConfig from "constants/apiConfig";
import { fetchCollaborationMilestone, fetchCollaborationTasks, fetchCollaborationTasksSummary } from "services/api/collaboration";
import { AuthState } from "slices/auth/reducer";
import { ERROR } from "types";
import { CollaborationItem, CollaborationsSlice, CollaborationTasksSummary, MilestoneData } from "types/collaboration";
import { generateUniqueKey, getErrorMessage, isFresh } from "utils/helpers";

export const fetchAllCollaborationsThunk = createAsyncThunk<{count: number, items: CollaborationItem[], gig_types: any[], gig_steps: any[], filters: { page: number, [key: string]: any }, filterId: string}, {filters: { page: number, [key: string]: any }, force?: boolean}, {rejectValue: string}>(
    'collaborations',
    async({filters, force}, {getState, rejectWithValue}) => {
        try {
            const state = getState() as { auth: AuthState, collaborations: CollaborationsSlice };
            const filterId = generateUniqueKey(filters, ['page']);
            const STALE_TIME = apiConfig.FETCH_COLLABORATION_TASKS.staleTime;

            if(filters.page === 1 && !force && isFresh(state.collaborations?.filtered_lists?.[filterId]?.last_fetched_at, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }

            const hubId = state.auth.selectedHubId;
            if(!hubId) throw new Error("No hub selected!");

            const {count, items, gig_types, gig_steps} = await fetchCollaborationTasks(hubId, {...filters, limit: 24});

            return {count, items, filters, filterId, gig_types, gig_steps}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch collaboration!"));
        }
    }
)

export const fetchCollaborationMilestoneData = createAsyncThunk<{data: MilestoneData, influencerId: string, campaignId: string, milestoneId: string, cache?: boolean}, {campaignId: string, milestoneId: string, influencerId: string, force?: boolean}, {rejectValue: string}>(
    'campaign/collaboration/milestone',
    async ({campaignId, influencerId, milestoneId, force}, { getState, rejectWithValue }) => {
        try { 
            const STALE_TIME = apiConfig.FETCH_COLLABORATION_MILESTONE.staleTime;
            const state = getState() as {auth: AuthState, collaborations: CollaborationsSlice};
            const existingData = state.collaborations.milestones?.[campaignId]?.[influencerId]?.[milestoneId] ?? null
            if(!force && isFresh(existingData?.last_fetched_at ?? null, STALE_TIME)){
                return {milestoneId, campaignId, influencerId, data: existingData.milestone, cache: true}
            }
            const hubId = state.auth.selectedHubId ?? '';
            const data = await fetchCollaborationMilestone(hubId, campaignId, influencerId, milestoneId);
            return {milestoneId, campaignId, influencerId, data};
        } catch (error) {
            return rejectWithValue('Failed to fetch collaboration milestone');
        }
    }
)

export const fetchCollaborationTasksSummaryThunk = createAsyncThunk<{ summary: CollaborationTasksSummary }, { force?: boolean }, { rejectValue: string }>(
    'collaboration/task/summary',
    async ({ force }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { auth: AuthState, collaborations: CollaborationsSlice };
            const STALE_TIME = apiConfig.FETCH_COLLABS_SUMMARY.staleTime;
            const selectedHubId = state.auth.selectedHubId;

            if(!selectedHubId) throw new Error("Hub not selected!");

            if (!force && isFresh(state.collaborations.summary.last_fetched_at, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }

            const summary = await fetchCollaborationTasksSummary(selectedHubId);

            return { summary }

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch collaboration task summary!"));
        }
    }
)
